import { useCallback, useMemo, useState } from 'react'
import { NetworkStatus, useQuery } from '@apollo/client'

import type { GetTopBrandsQuery } from 'generated/graphql'
import { useSearchParams } from 'hooks'
import { PanelStore } from 'stores'
import { analyticsTracker, localStorageUtils } from 'utils'

import { BrandRankingContext } from './context'
import { GET_TOP_BRANDS_QUERY } from './query'
import type { ChooseCategoryOptions } from './types'
import { checkActiveBrands, transformResponseToBrandRankingData } from './utils'
interface BrandRankingProviderProps {
  children: React.ReactNode
}
export const BrandRankingProvider = ({ children }: BrandRankingProviderProps) => {
  const selectedPanel = Object(PanelStore.selectedPanel)
  const [selectedWorkspaceCategory, setSelectedWorkspaceCategory] = useState(
    localStorageUtils.getObject(`category-${selectedPanel?.id}`)
  )
  const searchParams = useSearchParams()
  const activeBrandId = searchParams.get('activeBrandId')
  const { data, networkStatus, error, refetch } = useQuery<GetTopBrandsQuery>(GET_TOP_BRANDS_QUERY, {
    notifyOnNetworkStatusChange: true,
  })
  const brands = useMemo(() => {
    const selfBrand = data?.getWorkspace?.topBrandSOAByWorkspace?.selfBrandSOA
    const transformedData = transformResponseToBrandRankingData(data, selfBrand?.brand?.id)
    return checkActiveBrands(transformedData, activeBrandId)
  }, [activeBrandId, data])
  const chooseCategory = useCallback(
    (category: string, options: ChooseCategoryOptions = {}) => {
      const { mixpanelProps } = options
      if (mixpanelProps) {
        const { CAT } = analyticsTracker.eventNames
        analyticsTracker.eventTrack(CAT.CONFIGURE_CATEGORY, {
          ...mixpanelProps,
          Category: category,
        })
      }
      localStorageUtils.setObject(`category-${selectedPanel?.id}`, category)
      setSelectedWorkspaceCategory(category)
    },
    [selectedPanel?.id]
  )
  const isLoadingBrands = [NetworkStatus.loading, NetworkStatus.refetch].includes(networkStatus)
  const workspaceCategory = selectedPanel?.category ?? null
  const value = useMemo(
    () => ({
      brands,
      isLoading: isLoadingBrands,
      error: !!error,
      workspaceCategory,
      selectedWorkspaceCategory,
      refetch,
      chooseCategory,
    }),
    [brands, isLoadingBrands, error, workspaceCategory, selectedWorkspaceCategory, refetch, chooseCategory]
  )
  return <BrandRankingContext.Provider value={value}>{children}</BrandRankingContext.Provider>
}
